import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuShortcut,
	DropdownMenuTrigger,
} from "@/components/core/actions/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/core/avatar.tsx";
import { Notification } from "@/components/notifications";
import Omnisearch from "@/components/omnisearch/omnisearch";
import { userPermissions } from "@/constants/user-permissions";
import { userRoles } from "@/constants/user-roles";
import PinCodeModal from "@/features/case-evaluation/components/pin-code-modal";
import { HasPermission, HasRole, useAuth } from "@/hooks/use-auth.tsx";
import { queryClient } from "@/query-client";
import { IconHelp, IconLogout, IconSearch, IconSettings, IconSlash, IconUser } from "@tabler/icons-react";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { use } from "axios-request-throttle";
import clsx from "clsx";
import { useState } from "react";
import { useModal } from "../core/modals/use-modal";

function BccSidebar() {
	const { pathname } = useLocation();
	const navigate = useNavigate();

	const getActiveClass = (path: string) => {
		const pathName = pathname.split("/")[1];
		return path === pathName
			? "before:absolute before:bottom-0 before:w-full before:h-[2px] before:bg-primary-600 text-primary-700"
			: "text-foreground/70 hover:text-foreground";
	};

	return (
		<div className="inline-flex flex-row items-center w-auto h-full gap-4">
			<Link
				to={"/bcc-dashboard"}
				className={clsx(
					"relative inline-flex items-center h-full text-base font-semibold text-foreground/70 hover:text-foreground",
					getActiveClass("bcc-dashboard"),
				)}
			>
				<span className="inline-flex">Dashboard</span>
			</Link>
			<HasPermission permissions={[userPermissions.VIEW_CASE_FILING]}>
				<Link
					to={"/case-evaluation"}
					className={clsx(
						"relative inline-flex items-center h-full text-base font-semibold text-foreground/70 hover:text-foreground",
						getActiveClass("case-evaluation"),
					)}
				>
					<span className="inline-flex">Cases</span>
				</Link>
			</HasPermission>
			<HasPermission permissions={[userPermissions.SERVINGS]}>
				<Link
					to={"/serving"}
					className={clsx(
						"relative inline-flex items-center h-full text-base font-semibold text-foreground/70 hover:text-foreground",
						getActiveClass("serving"),
					)}
				>
					<span className="inline-flex">Services</span>
				</Link>
			</HasPermission>
			<HasPermission permissions={[userPermissions.CALENDAR]}>
				<Link
					to={"/calendar"}
					className={clsx(
						"relative inline-flex items-center h-full text-base font-semibold text-foreground/70 hover:text-foreground",
						getActiveClass("calendar"),
					)}
				>
					<span className="inline-flex">Calendar</span>
				</Link>
			</HasPermission>
			<HasPermission permissions={[userPermissions.ARTICLES]}>
				<Link
					to="/articles"
					search={{ status: "Draft", authors: "me" }}
					className={clsx(
						"relative inline-flex items-center h-full text-base font-semibold text-foreground/70 hover:text-foreground",
						getActiveClass("articles"),
					)}
				>
					<span className="inline-flex">Articles</span>
				</Link>
			</HasPermission>
			<HasPermission permissions={[userPermissions.REPORTS]}>
				<Link
					to={"/statistics"}
					className={clsx(
						"relative inline-flex items-center h-full text-base font-semibold text-foreground/70 hover:text-foreground",
						getActiveClass("statistics"),
					)}
				>
					<span className="inline-flex">Statistics</span>
				</Link>

				<DropdownMenu modal={false}>
					<DropdownMenuTrigger asChild>
						<button
							type="button"
							className={clsx(
								"relative inline-flex items-center h-full text-base font-semibold text-foreground/70 hover:text-foreground",
								getActiveClass("settings"),
							)}
						>
							<span className="inline-flex">Settings</span>
						</button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="start" sideOffset={-6}>
						<DropdownMenuGroup>
							<DropdownMenuItem
								onSelect={() => {
									navigate({
										to: "/settings/form-templates",
									});
								}}
							>
								<div className="flex flex-row items-center w-full py-1 px-2">Form Templates</div>
							</DropdownMenuItem>
							<DropdownMenuItem
								onSelect={() => {
									navigate({
										to: "/settings/help-categories",
									});
								}}
							>
								<div className="flex flex-row items-center w-full py-1 px-2">Help Categories</div>
							</DropdownMenuItem>
							<DropdownMenuItem
								onSelect={() => {
									navigate({
										to: "/help-topics",
									});
								}}
							>
								<div className="flex flex-row items-center w-full py-1 px-2">Help Topics</div>
							</DropdownMenuItem>
						</DropdownMenuGroup>
					</DropdownMenuContent>
				</DropdownMenu>
			</HasPermission>

			<HasRole roles={[userRoles.STENOGRAPHER]}>
				<Link
					to="/stenographic-notes"
					className={clsx(
						"relative inline-flex items-center h-full text-base font-semibold text-foreground/70 hover:text-foreground",
						getActiveClass("stenographic-notes"),
					)}
				>
					<span className="inline-flex">Stenographic Notes</span>
				</Link>
			</HasRole>

			<HasRole roles={[userRoles.SHERIFF]}>
				<Link
					to="/sheriff-reports"
					className={clsx(
						"relative inline-flex items-center h-full text-base font-semibold text-foreground/70 hover:text-foreground",
						getActiveClass("sheriff-reports"),
					)}
				>
					<span className="inline-flex">Sheriff's Reports</span>
				</Link>
			</HasRole>

			{/* <Link
				to={"/report-statistic"}
				className={clsx(
					"relative inline-flex items-center h-full text-base font-semibold text-foreground/70 hover:text-foreground",
					getActiveClass("report-statistic"),
				)}
			>
				<span className="inline-flex">Report Statistic</span>
			</Link> */}
		</div>
	);
}

function OccSidebar() {
	const { pathname } = useLocation();

	const getActiveClass = (path: string) => {
		const pathName = pathname.split("/")[1];
		return path === pathName
			? "before:absolute before:bottom-0 before:w-full before:h-[2px] before:bg-primary-600 text-primary-700"
			: "text-foreground/70 hover:text-foreground";
	};

	return (
		<div className="inline-flex flex-row items-center w-auto h-full gap-4">
			<Link
				to={"/occ-dashboard"}
				className={clsx(
					"relative inline-flex items-center h-full text-base font-semibold text-foreground/70 hover:text-foreground",
					getActiveClass("occ-dashboard"),
				)}
			>
				<span className="inline-flex">Dashboard</span>
			</Link>
			<Link
				to={"/case-classification"}
				className={clsx(
					"relative inline-flex items-center h-full text-base font-semibold text-foreground/70 hover:text-foreground",
					getActiveClass("case-classification"),
				)}
			>
				<span className="inline-flex">Cases</span>
			</Link>
			<Link
				to={"/raffle"}
				className={clsx("relative inline-flex items-center h-full text-base font-semibold text-foreground/70 hover:text-foreground", getActiveClass("raffle"))}
			>
				<span className="inline-flex">Raffle</span>
			</Link>
			<Link
				to={"/calendar"}
				className={clsx(
					"relative inline-flex items-center h-full text-base font-semibold text-foreground/70 hover:text-foreground",
					getActiveClass("calendar"),
				)}
			>
				<span className="inline-flex">Calendar</span>
			</Link>
			{/* <Link
				to={"/articles"}
				className={clsx(
					"relative inline-flex items-center h-full text-base font-semibold text-foreground/70 hover:text-foreground",
					getActiveClass("articles"),
				)}
			>
				<span className="inline-flex">Articles</span>
			</Link> */}
		</div>
	);
}

function AdminSidebar() {
	const { pathname } = useLocation();

	const getActiveClass = (path: string) => {
		const pathName = pathname.split("/")[1];
		return path === pathName
			? "before:absolute before:bottom-0 before:w-full before:h-[2px] before:bg-primary-600 text-primary-700"
			: "text-foreground/70 hover:text-foreground";
	};

	const navigate = useNavigate();
	return (
		<div className="inline-flex flex-row items-center w-auto h-full gap-4">
			<Link
				to={"/users"}
				className={clsx("relative inline-flex items-center h-full text-base font-semibold text-foreground/70 hover:text-foreground", getActiveClass("users"))}
			>
				<span className="inline-flex">Users</span>
			</Link>
			<Link
				to="/calendar"
				className={clsx(
					"relative inline-flex items-center h-full text-base font-semibold text-foreground/70 hover:text-foreground",
					getActiveClass("calendar"),
				)}
			>
				<span className="inline-flex">Calendar</span>
			</Link>
			{/* <Link
				to="/articles"
				className={clsx(
					"relative inline-flex items-center h-full text-base font-semibold text-foreground/70 hover:text-foreground",
					getActiveClass("articles"),
				)}
			>
				<span className="inline-flex">Articles</span>
			</Link> */}
			<DropdownMenu modal={false}>
				<DropdownMenuTrigger asChild>
					<button
						type="button"
						className={clsx(
							"relative inline-flex items-center h-full text-base font-semibold text-foreground/70 hover:text-foreground",
							getActiveClass("settings"),
						)}
					>
						<span className="inline-flex">Settings</span>
					</button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="start" sideOffset={-6}>
					<DropdownMenuGroup>
						<DropdownMenuItem
							onSelect={() => {
								navigate({
									to: "/settings/case-category",
								});
							}}
						>
							<div className="flex flex-row items-center w-full py-1 px-2">Case Category</div>
						</DropdownMenuItem>
						<DropdownMenuItem
							onSelect={() => {
								navigate({
									to: "/settings/case-type",
								});
							}}
						>
							<div className="flex flex-row items-center w-full py-1 px-2">Case Type</div>
						</DropdownMenuItem>
						<DropdownMenuItem
							onSelect={() => {
								navigate({
									to: "/settings/case-stages",
								});
							}}
						>
							<div className="flex flex-row items-center w-full py-1 px-2">Case Stages</div>
						</DropdownMenuItem>
						<DropdownMenuItem
							onSelect={() => {
								navigate({
									to: "/settings/court-level",
								});
							}}
						>
							<div className="flex flex-row items-center w-full py-1 px-2">Court Level</div>
						</DropdownMenuItem>
						<DropdownMenuItem
							onSelect={() => {
								navigate({
									to: "/settings/court-type",
								});
							}}
						>
							<div className="flex flex-row items-center w-full py-1 px-2">Court Type</div>
						</DropdownMenuItem>
						<DropdownMenuItem
							onSelect={() => {
								navigate({
									to: "/settings/court-type-station",
								});
							}}
						>
							<div className="flex flex-row items-center w-full py-1 px-2">Court Type Station</div>
						</DropdownMenuItem>
						<DropdownMenuItem
							onSelect={() => {
								navigate({
									to: "/settings/province",
								});
							}}
						>
							<div className="flex flex-row items-center w-full py-1 px-2">Province</div>
						</DropdownMenuItem>
						<DropdownMenuItem
							onSelect={() => {
								navigate({
									to: "/settings/region",
								});
							}}
						>
							<div className="flex flex-row items-center w-full py-1 px-2">Region</div>
						</DropdownMenuItem>
						<DropdownMenuItem
							onSelect={() => {
								navigate({
									to: "/settings/form-templates",
								});
							}}
						>
							<div className="flex flex-row items-center w-full py-1 px-2">Form Templates</div>
						</DropdownMenuItem>
						<DropdownMenuItem
							onSelect={() => {
								navigate({
									to: "/settings/article-categories",
								});
							}}
						>
							<div className="flex flex-row items-center w-full py-1 px-2">Article Categories</div>
						</DropdownMenuItem>
						<DropdownMenuItem
							onSelect={() => {
								navigate({
									to: "/settings/help-categories",
								});
							}}
						>
							<div className="flex flex-row items-center w-full py-1 px-2">Help Categories</div>
						</DropdownMenuItem>
						<DropdownMenuItem
							onSelect={() => {
								navigate({
									to: "/help-topics",
								});
							}}
						>
							<div className="flex flex-row items-center w-full py-1 px-2">Help Topics</div>
						</DropdownMenuItem>
						<DropdownMenuItem
							onSelect={() => {
								navigate({
									to: "/settings/roles",
								});
							}}
						>
							<div className="flex flex-row items-center w-full py-1 px-2">Roles</div>
						</DropdownMenuItem>
						<DropdownMenuItem
							onSelect={() => {
								navigate({
									to: "/settings/branch",
								});
							}}
						>
							<div className="flex flex-row items-center w-full py-1 px-2">Branch</div>
						</DropdownMenuItem>
						<DropdownMenuItem
							onSelect={() => {
								navigate({
									to: "/settings/keywords",
								});
							}}
						>
							<div className="flex flex-row items-center w-full py-1 px-2">Case Stage Keywords</div>
						</DropdownMenuItem>
						<DropdownMenuItem
							onSelect={() => {
								navigate({
									to: "/settings/user-audit-logs",
								});
							}}
						>
							<div className="flex flex-row items-center w-full py-1 px-2">User Audit Logs</div>
						</DropdownMenuItem>
						<DropdownMenuItem
							onSelect={() => {
								navigate({
									to: "/settings/case-activity-settings",
								});
							}}
						>
							<div className="flex flex-row items-center w-full py-1 px-2">Case Activity Settings</div>
						</DropdownMenuItem>
						<DropdownMenuItem
							onSelect={() => {
								navigate({
									to: "/settings/active-user-sessions",
								});
							}}
						>
							<div className="flex flex-row items-center w-full py-1 px-2">Active User Sessions</div>
						</DropdownMenuItem>
					</DropdownMenuGroup>
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	);
}

function DashboardUserSidebar() {
	const { pathname } = useLocation();

	const getActiveClass = (path: string) => {
		const pathName = pathname.split("/")[1];
		return path === pathName
			? "before:absolute before:bottom-0 before:w-full before:h-[2px] before:bg-primary-600 text-primary-700"
			: "text-foreground/70 hover:text-foreground";
	};

	return (
		<div className="inline-flex flex-row items-center w-auto h-full gap-4">
			<Link
				to={"/statistics"}
				className={clsx(
					"relative inline-flex items-center h-full text-base font-semibold text-foreground/70 hover:text-foreground",
					getActiveClass("statistics"),
				)}
			>
				<span className="inline-flex">Dashboard</span>
			</Link>
			<HasPermission permissions={[userPermissions.CALENDAR]}>
				<Link
					to={"/calendar-of-events"}
					className={clsx(
						"relative inline-flex items-center h-full text-base font-semibold text-foreground/70 hover:text-foreground",
						getActiveClass("calendar-of-events"),
					)}
				>
					<span className="inline-flex">Calendar</span>
				</Link>
			</HasPermission>
		</div>
	);
}

export function Topbar({
	className,
	pageName,
}: {
	className?: string;
	pageName?: string;
}) {
	const { logout, user } = useAuth();
	const modal = useModal();
	const navigate = useNavigate();
	const [isOmnisearchOpen, setOmnisearchOpen] = useState(false);
	const handleOpenOmnisearch = () => {
		setOmnisearchOpen(true);
	};

	const handleLogout = async () => {
		await logout();
		await queryClient.clear();
		if (import.meta.env.VITE_SCPORTAL_URL) {
			window.location.href = import.meta.env.VITE_SCPORTAL_URL;
		} else {
			await navigate({
				to: "/login",
			});
		}
	};

	const userBadge = user?.branches && user.branches.length > 0 ? user.branches.map((branch) => branch.assignable?.name).join(" / ") : "Admin";

	return (
		<div className={clsx("flex flex-col w-full h-[90px] border-b border-border/50 transition-transform duration-300", className)}>
			{/* main nav */}
			<div className="flex flex-row items-center w-full px-6 gap-12 shrink-0 h-[50px] bg-primary-50 border-b border-border/50 ">
				{/* <Link to="/" className="inline-flex flex-row items-center h-full gap-2"> */}
				<div className="inline-flex flex-row items-center h-full gap-2">
					<div className="inline-flex flex-row w-auto gap-1">
						<span className="text-lg font-bold text-gray-800">CMS</span>
						<div className="inline-flex flex-row items-center h-[20px] shrink-0 px-2 rounded-full bg-foreground-dark">
							<span className="text-2xs uppercase font-bold text-white">{userBadge}</span>
						</div>
					</div>
				</div>
				{/* </Link> */}
				<HasRole roles={[userRoles.SUPER_ADMIN]}>
					<AdminSidebar />
				</HasRole>
				<HasRole roles={[userRoles.OCC]}>
					<OccSidebar />
				</HasRole>
				<HasPermission permissions={[userPermissions.BCC]}>
					<BccSidebar />
				</HasPermission>
				<HasRole roles={[userRoles.STENOGRAPHER, userRoles.SHERIFF]}>
					<BccSidebar />
				</HasRole>
				{/* <HasRole roles={[userRoles.JUDGE]}>
  				<JudgeSidebar />
  			</HasRole> */}
				<HasRole roles={[userRoles.DASHBOARD]}>
					<DashboardUserSidebar />
				</HasRole>
				<div className="inline-flex flex-row items-center gap-4 ml-auto">
					<div className="inline-flex flex-row items-center gap-1">
						<div className="inline-flex pr-2 text-blue-500">
							<button
								type="button"
								onClick={() => {
									navigate({
										to: "/changelogs",
									});
								}}
							>
								<small>{import.meta.env.VITE_APP_VERSION}</small>
							</button>
						</div>
						<div className="inline-flex">
							{isOmnisearchOpen && <Omnisearch isOpen={isOmnisearchOpen} onClose={() => setOmnisearchOpen(false)} />}
							<button
								onClick={handleOpenOmnisearch}
								type="button"
								className="inline-flex justify-center items-center w-[32px] h-[32px] rounded-md bg-primary-100 hover:bg-primary-200 text-primary-600"
							>
								<IconSearch size={18} stroke={2} />
							</button>
						</div>
						<div className="inline-flex">
							<button
								onClick={() => {
									navigate({
										to: "/help-center",
									});
								}}
								type="button"
								className="relative inline-flex justify-center items-center w-[32px] h-[32px] rounded-md bg-primary-100 hover:bg-primary-200 text-primary-600"
							>
								<IconHelp size={18} stroke={2} />
							</button>
						</div>
						<div className="inline-flex">
							<Notification />
						</div>
					</div>

					<div className="inline-flex">
						<DropdownMenu modal={false}>
							<DropdownMenuTrigger asChild>
								<button type="button" className="inline-flex justify-center items-center rounded-full overflow-hidden hover:ring-2 hover:ring-focus-ring/30 ">
									<Avatar className="w-[32px] h-[32px] bg-primary-600">
										{!user?.avatar_url ? (
											<AvatarFallback className="text-xs font-bold">{user?.initials}</AvatarFallback>
										) : (
											<AvatarImage src={user?.avatar_url} />
										)}
									</Avatar>
								</button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end" sideOffset={5}>
								<div className="flex flex-row items-center w-full gap-2 p-2 bg-slate-50">
									<Avatar className="w-[44px] h-[44px] border-2 rounded-full overflow-hidden bg-primary-600">
										{!user?.avatar_url ? <AvatarFallback className="text-sm">{user?.initials}</AvatarFallback> : <AvatarImage src={user?.avatar_url} />}
									</Avatar>
									<div className="flex flex-col w-full">
										<span className="text-base font-semibold text-foreground-dark whitespace-nowrap">{user?.full_name}</span>
										<span className="text-base text-foreground/70 whitespace-nowrap">{user?.email}</span>
									</div>
								</div>
								<DropdownMenuSeparator />
								<DropdownMenuGroup>
									<DropdownMenuItem>
										<Link to="/profile" className="flex flex-row items-center w-full py-1 px-2">
											Profile
											<DropdownMenuShortcut>
												<IconUser size={18} stroke={1.5} />
											</DropdownMenuShortcut>
										</Link>
									</DropdownMenuItem>
									<DropdownMenuItem>
										<Link to="/settings" className="flex flex-row items-center w-full py-1 px-2">
											Settings
											<DropdownMenuShortcut>
												<IconSettings size={18} stroke={1.5} />
											</DropdownMenuShortcut>
										</Link>
									</DropdownMenuItem>
									<DropdownMenuItem>
										<button
											type="button"
											className="flex flex-row items-center w-full py-1 px-2"
											onClick={() => {
												modal.openModal({
													title: "Update Pin Code",
													description: "Please update your pin code to proceed.",
													size: "sm",
													component: <PinCodeModal />,
												});
											}}
										>
											Update Pin Code
											<DropdownMenuShortcut>
												<IconSettings size={18} stroke={1.5} />
											</DropdownMenuShortcut>
										</button>
									</DropdownMenuItem>
									<DropdownMenuItem>
										<Link to="/help-center" className="flex flex-row items-center w-full py-1 px-2">
											Help Center
											<DropdownMenuShortcut>
												<IconHelp size={18} stroke={1.5} />
											</DropdownMenuShortcut>
										</Link>
									</DropdownMenuItem>
								</DropdownMenuGroup>
								<DropdownMenuSeparator />
								<DropdownMenuGroup>
									<DropdownMenuItem onSelect={handleLogout} dangerZone>
										<div className="flex flex-row items-center w-full py-1 px-2">
											Logout
											<DropdownMenuShortcut>
												<IconLogout size={18} stroke={1.5} />
											</DropdownMenuShortcut>
										</div>
									</DropdownMenuItem>
								</DropdownMenuGroup>
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
				</div>
			</div>
			{/* breadcrumbs */}
			<div className="flex flex-row justify-center h-[40px] w-full bg-background">
				<div className="flex flex-row items-center w-full w-ba px-6 max-w-full">
					<div className="inline-flex flex-col w-auto">
						<div className="inline-flex flex-row items-center w-auto">
							<Link to="/" className="inline-flex text-base text-foreground/70 hover:text-foreground-dark">
								Dashboard
							</Link>
							{pageName && (
								<>
									<span className="text-foreground/30">
										<IconSlash size={14} stroke={2} />
									</span>
									<Link to="/" className="inline-flex text-base text-foreground/70 hover:text-foreground-dark">
										{pageName && pageName}
									</Link>
								</>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
